// src/lib/hmrc.js
// HMRC OAuth 2.0 helper for Quarterly
//
// Sandbox:    HMRC_BASE = https://test-api.service.hmrc.gov.uk
// Production: HMRC_BASE = https://api.service.hmrc.gov.uk  (swap when going live)
//
// Token exchange always goes via /api/hmrc-token (Vercel serverless)
// so the client secret never touches the browser.

const HMRC_BASE    = 'https://test-api.service.hmrc.gov.uk'
const CLIENT_ID    = process.env.REACT_APP_HMRC_CLIENT_ID
const REDIRECT_URI = process.env.REACT_APP_HMRC_REDIRECT_URI

// ── Step 1 ─────────────────────────────────────────────────────────────────
// Redirect user to HMRC Government Gateway to grant Quarterly permission
// Scopes needed for MTD ITSA quarterly submissions + read access
export function redirectToHMRC() {
  if (!CLIENT_ID || !REDIRECT_URI) {
    console.error('HMRC client ID or redirect URI not set in environment')
    return
  }

  const state = crypto.randomUUID()
  sessionStorage.setItem('hmrc_oauth_state', state)

  const params = new URLSearchParams({
    response_type: 'code',
    client_id:     CLIENT_ID,
    scope:         'read:self-assessment write:self-assessment',
    redirect_uri:  REDIRECT_URI,
    state,
  })

  window.location.href = `${HMRC_BASE}/oauth/authorize?${params.toString()}`
}

// ── Step 2 ─────────────────────────────────────────────────────────────────
// Exchange the authorisation code for access + refresh tokens
// Calls /api/hmrc-token (server-side) — client secret stays on server
export async function exchangeCodeForTokens(code) {
  const res = await fetch('/api/hmrc-token', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ grant_type: 'authorization_code', code }),
  })

  const data = await res.json()

  if (!res.ok) {
    throw new Error(data.error || 'Token exchange failed')
  }

  return {
    accessToken:  data.access_token,
    refreshToken: data.refresh_token,
    expiryTime:   new Date(Date.now() + data.expires_in * 1000).toISOString(),
  }
}

// ── Step 3 ─────────────────────────────────────────────────────────────────
// Refresh an expired access token using the stored refresh token
export async function refreshHMRCToken(refreshToken) {
  const res = await fetch('/api/hmrc-token', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ grant_type: 'refresh_token', refresh_token: refreshToken }),
  })

  const data = await res.json()

  if (!res.ok) {
    throw new Error(data.error || 'Token refresh failed')
  }

  return {
    accessToken: data.access_token,
    expiryTime:  new Date(Date.now() + data.expires_in * 1000).toISOString(),
  }
}

// ── Fraud prevention headers ───────────────────────────────────────────────
// Required by law on every MTD API call
// https://developer.service.hmrc.gov.uk/guides/fraud-prevention/
export function getFraudHeaders() {
  return {
    'Gov-Client-Connection-Method': 'WEB_APP_VIA_SERVER',
    'Gov-Client-Timezone':          Intl.DateTimeFormat().resolvedOptions().timeZone,
    'Gov-Vendor-Version':           'Quarterly=1.0.0',
    'Gov-Client-User-Agent':        navigator.userAgent,
  }
}
